import strictUriEncode from 'strict-uri-encode'
import { type Agent } from 'undici'
import Accounts from './accounts.js'
import Admin, { DataCenter } from './admin.js'
import Audit from './audit.js'
import DS from './ds.js'
import Fidm from './fidm.js'
import GigyaError from './gigya-error.js'
import * as DefaultHttpRequest from './helpers/default-http-request.js'
import sleep from './helpers/sleep.js'
import IDX from './idx.js'
import BaseParams from './interfaces/base-params.js'
import ErrorCode from './interfaces/error-code.js'
import GigyaResponse from './interfaces/gigya-response.js'
import ProxyHttpRequest from './interfaces/proxy-http-request.js'
import Reports from './reports.js'
import { RequestFactory } from './RequestFactory.js'
import { AnonymousRequestSigner, isAnonymous, NoCredentials } from './requestsSigners/AnonymousRequestSigner.js'
import { AuthBearerSigner, isRSACreds, RSACredentials } from './requestsSigners/AuthBearerSigner.js'
import { isCredentials } from './requestsSigners/AuthRequestSigner.js'
import { ISigner } from './requestsSigners/ISigner.js'
import { hasPartnerSecret, PartnerSecret, PartnerSecretSigner } from './requestsSigners/PartnerSecretSigner.js'
import { isSecretCredentials, SecretCredentials, SimpleRequestSigner } from './requestsSigners/SimpleRequestSigner.js'
import SigUtils from './sig-utils.js'
import Socialize from './socialize.js'

export * from './accounts.b2b.auth.js'
export * from './accounts.b2b.js'
export * from './accounts.b2b.setup.js'
export * from './accounts.b2b.webhooks.js'
export * from './accounts.communication.js'
export * from './accounts.groups.js'
export * from './accounts.js'
export * from './admin.js'
export * from './ds.js'
export * from './fidm.js'
export * from './gigya-error.js'
export * from './idx.js'
export * from './interfaces/base-params.js'
export * from './interfaces/error-code.js'
export * from './interfaces/gigya-response.js'
export * from './interfaces/proxy-http-request.js'
export * from './reports.js'
export * from './sig-utils.js'
export * from './socialize.js'

export interface FormatJsonRequest {
	format: 'json'
}

export interface SignedRequestParams {
	timestamp: number
	nonce: number
	sig: string
}

export type RequestParams = FormatJsonRequest &
	Partial<SignedRequestParams> & { [key: string]: string | null | number | boolean }

export type CredentialsType =
	| NoCredentials
	| { secret: PartnerSecret }
	| SecretCredentials
	| RSACredentials

export class Gigya {
	protected static readonly RATE_LIMIT_SLEEP = 2000
	protected static readonly RETRY_LIMIT = 5
	protected static readonly RETRY_DELAY = 5000

	protected _apiKey: string | undefined

	protected httpRequest: ProxyHttpRequest
	public readonly sigUtils: SigUtils
	public readonly admin: Admin
	public readonly audit: Audit
	public readonly socialize: Socialize
	public readonly accounts: Accounts
	public readonly ds: DS
	public readonly fidm: Fidm
	public readonly reports: Reports
	public readonly idx: IDX

	protected _signer: ISigner | undefined
	protected _fetchAgent: Agent | undefined

	/**
	 * Initialize new instance of Gigya.
	 */
	constructor()
	constructor(proxyHttpRequest: ProxyHttpRequest)
	constructor(apiKey: string, dataCenter: DataCenter, proxy?: ProxyHttpRequest)
	constructor(apiKey: string, dataCenter: DataCenter, secret: string)
	constructor(apiKey: string, dataCenter: DataCenter, userKey: string, secret?: string)
	constructor(apiKey: string, dataCenter: DataCenter, credentials: RSACredentials)
	constructor(
		apiKeyOrProxy?: string | ProxyHttpRequest,
		protected _dataCenter: DataCenter = 'us1',
		userKeyOrSecretOrCredentialsOrProxy?: string | RSACredentials | ProxyHttpRequest,
		secret?: string
	) {
		let creds: CredentialsType = false

		// Work with overload signature.
		if (typeof apiKeyOrProxy === 'function') {
			this.httpRequest = apiKeyOrProxy
		} else if (apiKeyOrProxy) {
			this._apiKey = apiKeyOrProxy

			switch (typeof userKeyOrSecretOrCredentialsOrProxy) {
				case 'object':
					creds = userKeyOrSecretOrCredentialsOrProxy as RSACredentials
					break
				case 'string':
					if (!secret) {
						creds = { secret: userKeyOrSecretOrCredentialsOrProxy as PartnerSecret }
					} else {
						creds = {
							userKey: userKeyOrSecretOrCredentialsOrProxy,
							secret
						} as SecretCredentials
					}
					break
				case 'function': // proxy
					this.httpRequest = userKeyOrSecretOrCredentialsOrProxy
				// fallthrough
				default:
					creds = false as NoCredentials
			}
		}

		// Late-initialize default proxy to support browser-based environments.
		// Should not typically be used instead of Gigya JS SDK for public-facing sites.
		// Designed for environments where access is given directly to API in browser but request is proxied through server for credentials.
		// @ts-ignore
		if (!this.httpRequest) {
			this.httpRequest = DefaultHttpRequest.httpRequest
		}

		// Initialize sub-classes.
		this.sigUtils = new SigUtils(secret)
		this.admin = new Admin(this)
		this.audit = new Audit(this)
		this.socialize = new Socialize(this)
		this.accounts = new Accounts(this)
		this.ds = new DS(this)
		this.fidm = new Fidm(this)
		this.reports = new Reports(this)
		this.idx = new IDX(this)

		this.setCredentials(creds)
	}

	public setCredentials(credentials: CredentialsType): this {
		const signer = this.getSigner(credentials)
		if (!signer) throw 'unsupported credentials'
		this._signer = signer
		return this
	}

	protected getSigner(credentials: CredentialsType): ISigner | null {
		if (isAnonymous(credentials)) {
			return new AnonymousRequestSigner()
		} else if (hasPartnerSecret(credentials)) {
			return new PartnerSecretSigner(credentials.secret as PartnerSecret)
		} else if (isCredentials(credentials)) {
			if (isRSACreds(credentials)) {
				return new AuthBearerSigner(credentials)
			} else if (isSecretCredentials(credentials)) {
				return new SimpleRequestSigner(credentials)
			} else {
				throw 'missing secret/privateKey'
			}
		}

		return null
	}

	public setFetchAgent(fetchAgent?: Agent) {
		this._fetchAgent = fetchAgent
	}

	/**
	 * Make request to Gigya. Typically, you'll want to use the defined interface (for example gigya.accounts.getAccountInfo) instead of calling request directly.
	 *
	 * If a method is not available, create an issue or pull request at: https://github.com/scotthovestadt/gigya
	 */
	public async request<R>(endpoint: string, userParams: any = {}): Promise<GigyaResponse & R> {
		return this._request<R>(endpoint, userParams)
	}

	/**
	 * Internal handler for requests.
	 */
	protected async _request<R>(
		endpoint: string,
		userParams: BaseParams & { [key: string]: any },
		retries = 0
	): Promise<GigyaResponse & R> {
		const requestFactory = new RequestFactory(
			userParams.apiKey || this._apiKey,
			userParams.dataCenter || this._dataCenter
		)

		const request = requestFactory.create(endpoint, userParams)

		if (!request.skipSigning) {
			const signer = this.getSigner(userParams as CredentialsType) || this._signer
			if (signer) {
				signer.sign(request)
			}
		}

		// Fire request.
		let response
		try {
			response = await this.httpRequest<R>(
				request.endpoint,
				request.host,
				request.params,
				request.headers,
				this._fetchAgent
			)

			// Non-zero error code means failure.
			if (response.errorCode !== 0) {
				throw this.createErrorFromResponse(response, endpoint, userParams)
			}
		} catch (e) {
			const gigyaError: GigyaError = e as GigyaError
			// Check for error codes that signal need to retry.
			if (
				gigyaError.errorCode === ErrorCode.GENERAL_SERVER_ERROR ||
				gigyaError.errorCode === ErrorCode.SEARCH_TIMED_OUT ||
				gigyaError.errorCode === ErrorCode.CONCURRENT_UPDATES_NOT_ALLOWED
			) {
				retries++
				if (retries < Gigya.RETRY_LIMIT) {
					if (Gigya.RETRY_DELAY) {
						await sleep(Gigya.RETRY_DELAY)
					}
					return this._request<R>(endpoint, userParams, retries)
				}
			}
			throw gigyaError
		}

		// Check for rate limiting.
		if (response.errorCode === ErrorCode.RATE_LIMIT_HIT.valueOf()) {
			// Try again after waiting.
			await sleep(Gigya.RATE_LIMIT_SLEEP)
			return this._request<R>(endpoint, userParams, retries)
		}

		// Ensure Gigya returned successful response. If not, throw error with details.
		if (
			response.errorCode !== ErrorCode.SUCCESS &&
			response.errorCode !== undefined && // exportUsers doesn't return an error code
			response.errorCode !== ErrorCode.PENDING_REGISTRATION &&
			response.errorCode !== ErrorCode.PENDING_VERIFICATION
		) {
			throw this.createErrorFromResponse(response, endpoint, userParams)
		}

		// Return Gigya's successful response.
		return response
	}

	protected createRequestSignature(secret: string, uri: string, requestParams: RequestParams) {
		const httpMethod = DefaultHttpRequest.httpMethod.toUpperCase()
		const queryString = Object.keys(requestParams)
			.sort()
			.map((key) => `${key}=${strictUriEncode((requestParams[key] || '').toString())}`)
			.join('&')
		const baseString = `${httpMethod}&${strictUriEncode(uri)}&${strictUriEncode(queryString)}`
		return this.sigUtils.calcSignature(baseString, secret)
	}

	/**
	 * Create GigyaError from response.
	 */
	protected createErrorFromResponse(
		response: GigyaResponse,
		endpoint: string,
		params: BaseParams & Object
	): GigyaError {
		// Create meaningful error message.
		let errorMessage = `Gigya API ${endpoint} failed with error code ${response.errorCode}`
		const errorDetails = response.errorDetails ? response.errorDetails : response.errorMessage
		if (errorDetails) {
			errorMessage += ` and message ${errorDetails}`
		}
		if (response.validationErrors) {
			errorMessage += ':'
			for (const validationError of response.validationErrors) {
				errorMessage += ` ${validationError.fieldName}: ${validationError.message}`
			}
		}

		return new GigyaError(errorMessage, response, response.errorCode, params)
	}
}

export default Gigya
